// AdminCronStatus — Live-Snapshot der Cron-Aggregator-Keys.
// Quelle: GET /admin/cron-status (eve_market_cache je key: updated_at + size).

import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';

interface CronStatusRow {
  key: string;
  updated_at: string | null;
  age_sec: number | null;
  payload_size: number;
}

const EXPECTED_FRESHNESS_SEC: Record<string, number> = {
  // 1min Cron
  data: 90,
  // 5min Cron
  fred: 8 * 60,
  fred_us: 8 * 60,
  cot: 8 * 60,
  forex_news: 8 * 60,
  lunar_top: 8 * 60,
  news: 8 * 60,
  'klines-forex': 8 * 60,
  // stündliche/tägliche kommen seltener
  klines: 90 * 60,
  event_scores: 90 * 60,
  lunar: 90 * 60,
};

function freshness(row: CronStatusRow): { tone: string; label: string } {
  const expected = EXPECTED_FRESHNESS_SEC[row.key] ?? 24 * 60 * 60;
  const age = row.age_sec ?? Number.MAX_SAFE_INTEGER;
  if (age <= expected) {
    return { tone: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300', label: 'Live' };
  }
  if (age <= expected * 3) {
    return { tone: 'border-amber-500/30 bg-amber-500/10 text-amber-300', label: 'Verzögert' };
  }
  return { tone: 'border-rose-500/30 bg-rose-500/10 text-rose-300', label: 'Stale' };
}

function fmtAge(sec: number | null): string {
  if (sec === null) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86_400) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / 86_400)}d`;
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function AdminCronStatus() {
  const accessToken = useAuthStore(selectAccessToken);
  const [rows, setRows] = useState<CronStatusRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    if (!accessToken) return;
    setLoading(true);
    try {
      const data = await apiFetch<CronStatusRow[]>('/admin/cron-status', { accessToken });
      setRows(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return (
    <div className="glass-strong rounded-2xl p-5">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold">Cron-Status</h2>
          <div className="mt-1 text-[11px] text-neutral-500">
            {rows.length.toLocaleString('en-US')} Cache-Keys ·{' '}
            {rows.filter((r) => freshness(r).label === 'Live').length} live ·{' '}
            {rows.filter((r) => freshness(r).label === 'Stale').length} stale
          </div>
        </div>
        <button
          type="button"
          onClick={() => void reload()}
          aria-label="Aktualisieren"
          className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-2.5 py-1 text-[12px] text-neutral-300 hover:bg-white/5"
        >
          <RefreshCw className={`size-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error ? (
        <div className="rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
          {error}
        </div>
      ) : loading ? (
        <div className="py-8 text-center text-[12px] text-neutral-500">Lade…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-[12px] text-neutral-500">
          Keine Cache-Keys in eve_market_cache.
        </div>
      ) : (
        <div className="grid gap-2 md:grid-cols-2">
          {rows.map((r) => {
            const f = freshness(r);
            return (
              <div key={r.key} className="glass rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[12px] font-bold text-amber-300">{r.key}</span>
                  <span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold ${f.tone}`}>
                    {f.label}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] text-neutral-500">
                  <span>Age: {fmtAge(r.age_sec)}</span>
                  <span>{fmtSize(r.payload_size)}</span>
                </div>
                {r.updated_at ? (
                  <div className="mt-0.5 text-[10px] text-neutral-600">{r.updated_at}</div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
